import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { WorkflowAction } from './application-package.service';
import { ToastrService } from 'ngx-toastr';
import { PermitProjectFacilityAopService } from './permit-project-facility-aop.service';

@Injectable({
  providedIn: 'root'
})
export class PermitAopService {

  private aopSource = new BehaviorSubject<PermitAnnualOperatingPlanApi>(null);
  currentAop = this.aopSource.asObservable();
  aop: PermitAnnualOperatingPlanApi = null;

  constructor(
    @Inject('BASE_URL') private baseUrl: string,
    private http: HttpClient,
    private toastrService: ToastrService,
    private pfaopService: PermitProjectFacilityAopService
  ) { }

  getAop(aopId?: number) {
    // Made aopId optional so that if the aop definitely exists already, the aopId can be taken from the the service
    this.http.get<PermitAnnualOperatingPlanApi>(`${this.baseUrl}api/permitannualoperatingplan/${aopId || this.aop.permitAnnualOperatingPlanId}`).subscribe(response => {
      if (response) {
        this.aop = response;
        this.changeAop(this.aop);
        this.pfaopService.getPfaops(aopId || this.aop.permitAnnualOperatingPlanId);
      } else {
        this.changeAop(null);
        this.aop = null;
      }
    },
      error => console.error(error)
    );
  }

  putAop(data: PermitAnnualOperatingPlanApi) {
    var dataPut: any = {
      permitAnnualOperatingPlanId: data.permitAnnualOperatingPlanId,
      facilityUseIrrigation: data.facilityUseIrrigation,
      facilityUseIrrigationBegin: data.facilityUseIrrigationBegin,
      facilityUseIrrigationEnd: data.facilityUseIrrigationEnd,
      facilityUseIrrigationDeliveryBegin: data.facilityUseIrrigationDeliveryBegin,
      facilityUseIrrigationDeliveryEnd: data.facilityUseIrrigationDeliveryEnd,
      facilityUseIrrigationNarrative: data.facilityUseIrrigationNarrative,
      facilityUseIrrigationFileUploadListId: data.facilityUseIrrigationFileUploadListId,
      facilityUseHydro: data.facilityUseHydro,
      facilityUseHydroBegin: data.facilityUseHydroBegin,
      facilityUseHydroEnd: data.facilityUseHydroEnd,
      facilityUseHydroNarrative: data.facilityUseHydroNarrative,
      facilityUseHydroFileUploadListId: data.facilityUseHydroFileUploadListId,
      facilityUseStorage: data.facilityUseStorage,
      facilityUseStorageBegin: data.facilityUseStorageBegin,
      facilityUseStorageEnd: data.facilityUseStorageEnd,
      facilityUseStorageNarrative: data.facilityUseStorageNarrative,
      facilityUseStorageFileUploadListId: data.facilityUseStorageFileUploadListId,
      facilityUseNonExcessFlowActivitiesNarrative: data.facilityUseNonExcessFlowActivitiesNarrative,
    };
    this.http.put<any>(`${this.baseUrl}api/permitannualoperatingplan`, dataPut).subscribe(
      response => {
        if (response) {
          this.aop = response;
          this.changeAop(this.aop);
          this.toastrService.success("General System Operations updated.");
        } else {
          this.changeAop(null);
          this.aop = null;
        }
      },
      error => console.error(error)
    );
  }

  // POST AOP NOW A PART OF APPLICATION PACKAGE SERVICE
  workflowAOP (action: string, comment = '') {
    this.http.post(`${this.baseUrl}api/permitannualoperatingplan/${this.aop.permitAnnualOperatingPlanId}/workflow`, { WorkflowAction: action, comment: comment }, {observe: 'response'}).subscribe(
      response => {
        if (response.status === 200) {
          this.toastrService.success(`Successfully executed the '${action}' workflow for this Permit AOP!`, 'Success:', {
            positionClass: 'toast-top-full-width',
            closeButton: true,
          });
          this.getAop();
        }
      },
      error => console.error(error)
    );
  }

  submitWaterRightsId(waterRightsId: string, applicationNumber: string, permitTypeName: string) {
    let data: ApplicationAnnualOperatinPlanWaterRightsIdApi = {
      permitAnnualOperatingPlanId: this.aop.permitAnnualOperatingPlanId,
      waterRightsId: waterRightsId,
      applicationNumber: applicationNumber
    };
    this.http.post(`${this.baseUrl}api/permitannualoperatingplan/setwaterrightsid`, data, { observe: 'response' }).subscribe(response => {
      if (response.status === 200) {
        if (permitTypeName === 'permanent' || permitTypeName === 'Permanent') {
          this.workflowAOP('ApprovePermanent');
        } else {
          this.workflowAOP('AcceptTemporary');
        }
      }
    });
  }

  submitStartEndDate(permitStartDate: Date, permitEndDate: Date) {
    let data: ApplicationAnnualOperatinPlanPermitStartEndDateApi = {
      permitAnnualOperatingPlanId: this.aop.permitAnnualOperatingPlanId,
      permitStartDate: permitStartDate,
      permitEndDate: permitEndDate
    };
    this.http.post(`${this.baseUrl}api/permitannualoperatingplan/setstartenddate`, data, { observe: 'response' }).subscribe(response => {
      if (response.status === 200) {
        this.workflowAOP('Approve');
      }
    });
  }


  changeAop(aop: PermitAnnualOperatingPlanApi) {
    this.aopSource.next(aop);
    this.aop = aop;
  }

}

export interface PermitAnnualOperatingPlanApi {
  permitAnnualOperatingPlanId: number,
  applicationPackageId?: number,
  createdDateTime?: Date,
  facilityUseIrrigation?: boolean,
  facilityUseIrrigationBegin?: Date,
  facilityUseIrrigationEnd?: Date,
  facilityUseIrrigationDeliveryBegin?: Date,
  facilityUseIrrigationDeliveryEnd?: Date,
  facilityUseIrrigationNarrative: string,
  facilityUseIrrigationFileUploadListId: number,
  facilityUseHydro?: boolean,
  facilityUseHydroBegin?: Date,
  facilityUseHydroEnd?: Date,
  facilityUseHydroNarrative: string,
  facilityUseHydroFileUploadListId: number,
  facilityUseStorage?: boolean,
  facilityUseStorageBegin?: Date,
  facilityUseStorageEnd?: Date,
  facilityUseStorageNarrative: string,
  facilityUseStorageFileUploadListId: number,
  facilityUseNonExcessFlowActivitiesNarrative: string,
  permitAnnualOperatingPlanStatusId: number,
  permitAnnualOperatingPlanStatusName: string,
  permitAnnualOperatingPlanYearId?: number,
  permitAnnualOperatingPlanYear: number,
  submittedUserName: string,
  submittedTimestamp?: Date,
  acceptedUserName: string,
  acceptedTimestamp?: Date,
  approvedUserName: string,
  approvedTimestamp?: Date,
  canEdit: boolean,
  allowedWorkflowActions?: WorkflowAction[]
}

export interface ApplicationAnnualOperatinPlanWaterRightsIdApi {
  permitAnnualOperatingPlanId: number, // int
  applicationNumber: string,
  waterRightsId: string // string
}

export interface ApplicationAnnualOperatinPlanPermitStartEndDateApi {
  permitAnnualOperatingPlanId: number,
  permitStartDate: Date,
  permitEndDate: Date
}

export enum PermitAnnualOperatingPlanWorkflowAction {
  Submit = "Submit",
  AcceptTemporary = "AcceptTemporary",
  ApprovePermanent = "ApprovePermanent",
  DeclineAcceptance = "DeclineAcceptance",
  Approve = "Approve",
  Deny = "Deny",
  ResetApplication = "ResetApplication",
  Withdraw = "Withdraw",
  ReopenApplication = "ReopenApplication"
}
